import React, { useState, useMemo, useEffect } from "react";
import { ARCHETYPES, MODIFIERS, SPEED_SETTINGS } from "../data/archetypes";
import { NFL_TEAMS } from "../data/players";
import { colors, fonts, spacing, radius, fontSize, fontWeight } from "../data/theme";
import LeagueChip from "./LeagueChip";
import { captureSetupNudgeClicked } from "../utils/onboardingAnalytics";
import { computeLeagueMarketProfile, describeMarket } from "../utils/leagueMarket";
import DraftHistoryModal from "./DraftHistoryModal";
import KeeperEntry from "./KeeperEntry";
import { validateKeepers, shouldPromoteKeeperSection } from "../utils/keepers";
import { isQbPremiumFormat } from "../utils/leagueFormat";
import { opponentSeats, _roomRule } from "../engine/simulation";
import PositionDials from "./PositionDials";
import MarketAnchor from "./MarketAnchor";
import {
  defaultAnchorForFormat, marketAnchorPresetPairs, marketAnchorPresetFor,
} from "../utils/marketAnchor";

const ARCHETYPE_META = {
  "Balanced":       { icon: "⚖️",  desc: "Even spend, no extreme bets",                           color: "#3b82f6" },
  "RB Heavy":       { icon: "🏃",  desc: "Loads up on RBs early",                                 color: "#22c55e" },
  "WR Heavy":       { icon: "🎯",  desc: "Prioritizes elite WRs",                                 color: "#f97316" },
  "Stars & Scrubs": { icon: "⭐",  desc: "2-3 big names, rest at $1",                             color: "#eab308" },
  "Zero RB":        { icon: "🚫",  desc: "Ignores RB, stacks WR and QB",                          color: "#ef4444" },
  "Hero RB":        { icon: "🦸",  desc: "One elite RB, then pivots",                             color: "#8b5cf6" },
  "The Hammer":     { icon: "🔨",  desc: "Waits for budget superiority, then takes over the draft", color: "#dc2626" },
};

const MAIN_ARCHETYPES = ["Balanced", "RB Heavy", "WR Heavy", "Stars & Scrubs", "Zero RB", "Hero RB", "The Hammer"];

const QB_STRATS     = ["Elite QB", "Balanced QB", "Punt QB"];
const QB_STRATS_SF  = ["Elite QB SF", "Balanced QB SF", "Punt QB SF"];
const TE_STRATS     = ["Elite TE", "Balanced TE", "Punt TE"];

const PLAY_STYLES = [
  { key: "Homer",          icon: "🏈", label: "Homer",    desc: "Overpays for their favorite NFL team" },
  { key: "Enforcer",       icon: "💪", label: "Enforcer", desc: "Bids up players they don't want to drain your budget" },
  { key: "Patient Spender",icon: "⏳", label: "Patient",  desc: "Sits out early, strikes mid-draft" },
];

function StratPills({ label, options, active, onSelect }) {
  return (
    <div style={{ marginBottom: spacing.lg }}>
      <div style={{
        fontSize: fontSize.micro, fontWeight: fontWeight.bold,
        color: colors.text.secondary, letterSpacing: "0.5px", marginBottom: spacing.xs,
      }}>{label}</div>
      <div style={{ display: "flex", gap: spacing.xs }}>
        {options.map(opt => {
          const sel = active === opt;
          // strip " SF" suffix for display
          const display = opt.replace(" SF", "");
          return (
            <button key={opt} onClick={() => onSelect(opt)}
              style={{
                flex: 1, padding: `${spacing.xs}px ${spacing.sm}px`,
                borderRadius: radius.sm, cursor: "pointer",
                fontSize: fontSize.caption, fontWeight: fontWeight.bold,
                fontFamily: "inherit", whiteSpace: "nowrap",
                background: sel ? "rgba(255,107,0,0.12)" : colors.bg.app,
                color: sel ? colors.brand.accent : colors.text.secondary,
                border: `1px solid ${sel ? colors.border.brandLight : colors.border.subtle}`,
                transition: "all 0.1s ease",
              }}>{display}</button>
          );
        })}
      </div>
    </div>
  );
}

function FineTuning({ mods, cfg, isSflex, updateCfg, toggleMod }) {
  const qbOptions = isSflex ? QB_STRATS_SF : QB_STRATS;
  const activeQB = qbOptions.find(o => mods.includes(o)) || (isSflex ? "Balanced QB SF" : "Balanced QB");
  const activeTE = TE_STRATS.find(o => mods.includes(o)) || "Balanced TE";

  const setQB = (val) => {
    const allQB = [...QB_STRATS, ...QB_STRATS_SF];
    updateCfg({ modifiers: [...(mods.filter(m => !allQB.includes(m))), val] });
  };
  const setTE = (val) => {
    updateCfg({ modifiers: [...(mods.filter(m => !TE_STRATS.includes(m))), val] });
  };

  const activeHomer    = mods.includes("Homer");
  const activeEnforcer = mods.includes("Enforcer");
  const activePatient  = mods.includes("Patient Spender");

  const toggleStyle = (key) => {
    if (key === "Enforcer" && !activeEnforcer) {
      // remove Patient if enabling Enforcer
      updateCfg({ modifiers: [...mods.filter(m => m !== "Patient Spender" && m !== "Enforcer"), "Enforcer"] });
    } else if (key === "Patient Spender" && !activePatient) {
      // remove Enforcer if enabling Patient
      updateCfg({ modifiers: [...mods.filter(m => m !== "Enforcer" && m !== "Patient Spender"), "Patient Spender"] });
    } else {
      toggleMod(key);
    }
  };

  const styleActive = { Homer: activeHomer, Enforcer: activeEnforcer, "Patient Spender": activePatient };

  return (
    <div style={{
      marginBottom: spacing.lg, paddingTop: spacing.md,
      borderTop: `1px solid ${colors.border.subtle}`,
    }}>
      <div style={{
        fontSize: fontSize.micro, fontWeight: fontWeight.bold,
        color: colors.text.secondary, letterSpacing: "1px", marginBottom: spacing.md,
      }}>FINE-TUNING</div>

      <StratPills label="QB STRATEGY" options={qbOptions} active={activeQB} onSelect={setQB} />
      <StratPills label="TE STRATEGY" options={TE_STRATS} active={activeTE} onSelect={setTE} />

      {/* Play Style chips */}
      <div style={{ marginBottom: spacing.md }}>
        <div style={{
          fontSize: fontSize.micro, fontWeight: fontWeight.bold,
          color: colors.text.secondary, letterSpacing: "0.5px", marginBottom: spacing.sm,
        }}>PLAY STYLE</div>
        <div style={{ display: "flex", flexDirection: "column", gap: spacing.sm }}>
          {PLAY_STYLES.map(({ key, icon, label, desc }) => {
            const sel = styleActive[key];
            return (
              <div key={key}>
                <button onClick={() => toggleStyle(key)}
                  style={{
                    display: "flex", alignItems: "center", gap: spacing.sm,
                    width: "100%", padding: `${spacing.sm}px ${spacing.md}px`,
                    borderRadius: radius.sm, cursor: "pointer", textAlign: "left",
                    background: sel ? "rgba(255,107,0,0.10)" : colors.bg.app,
                    border: `1px solid ${sel ? colors.border.brandLight : colors.border.subtle}`,
                    fontFamily: "inherit", transition: "all 0.1s ease",
                  }}>
                  <span style={{ fontSize: 14 }}>{icon}</span>
                  <span style={{
                    fontSize: fontSize.caption, fontWeight: fontWeight.bold,
                    color: sel ? colors.brand.accent : colors.text.primary,
                    flex: 1,
                  }}>{label}</span>
                  {sel && <span style={{ fontSize: 10, color: colors.brand.accent }}>✓</span>}
                </button>
                <div style={{
                  fontSize: 10, color: colors.text.muted, lineHeight: 1.4,
                  padding: `3px ${spacing.md}px 0`,
                }}>{desc}</div>
                {/* Homer team dropdown */}
                {key === "Homer" && sel && (
                  <select value={cfg.favoriteTeam || ""}
                    onChange={e => updateCfg({ favoriteTeam: e.target.value })}
                    style={{
                      marginTop: spacing.xs, width: "100%",
                      padding: `${spacing.xs}px ${spacing.md}px`,
                      borderRadius: radius.sm, fontSize: fontSize.caption,
                      fontFamily: "inherit", background: colors.bg.app,
                      border: `1px solid ${colors.border.brandLight}`,
                      color: colors.text.primary, cursor: "pointer",
                    }}>
                    <option value="">— Pick a team —</option>
                    {NFL_TEAMS.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

const MockSetup = React.memo(function MockSetup({
  mockTeamConfigs, setMockTeamConfigs,
  mockUserOwner, setMockUserOwner,
  mockUserSlot, setMockUserSlot,
  mockSpeed, setMockSpeed,
  mockNomTimerMode, setMockNomTimerMode,
  startMockDraft, onSimulate, onOpenSettings, onCreateLeague,
  mockSavedDraft, onClearSavedDraft, resumeMockDraft,
  settings, teamCount, activeLeagueName,
  activeLeague, onSetCalibrate, modeledSeatCount = 0,
  onReopenWizard,
  positionDials, onSetPositionDials, dialsOverrideLeague = false,
  marketAnchor, onSetMarketAnchor, marketAnchorEcho = null,
}) {
  const [showExpert, setShowExpert] = useState(false);
  const [editingTeam, setEditingTeam] = useState(null);
  const [showHistory, setShowHistory] = useState(false);

  // Null for hand-built leagues, imports with no prior seasons, and anything
  // under the pick threshold — the whole card is then omitted rather than
  // rendered empty, so those setups look exactly as they did before.
  const marketProfile = useMemo(
    () => computeLeagueMarketProfile(activeLeague?.auctionHistory),
    [activeLeague?.auctionHistory]
  );
  const marketClauses = useMemo(() => describeMarket(marketProfile), [marketProfile]);

  // ── WHO DRAFTS: one derivation, shared with the engine ────────────────────
  // `opponentSeats` (engine/simulation.js, right above buildDraftTeams) is the
  // only implementation of the seat rule, and buildDraftTeams calls it too — so
  // this list IS the scoreboard by construction.
  //
  // It used to be a hand-rolled `TEAM_NAMES.slice().filter().slice()` here that
  // skipped the canonical-room rule. Under a display-name collision the two
  // disagreed by exactly one seat each way: with the user as "Mike" in a
  // 10-team room, the panel offered "Steve" — who never drafts — and hid the
  // "Mike (CPU)" seat that does. Every consumer below now reads this one list.
  const opponentPool = useMemo(
    () => opponentSeats(teamCount, mockUserOwner),
    [teamCount, mockUserOwner]
  );

  // ── First look at your league's seats ─────────────────────────────────────
  // An imported league's opponents ARE the user's leaguemates, named and (when
  // calibrated) fitted — and all of that sits behind a collapsed panel nobody
  // opens. Open it once, so the payoff of importing is visible without a click.
  //
  // Gated on the seats actually being seeded, not just on the league having
  // members: seedMemberNames runs across two renders (see 49dd820), and opening
  // early would show default TEAM_NAMES — the opposite of the point.
  //
  // The seen-marker lives on the configs blob under a "__" key, which
  // sanitizeTeamConfigs already carries through verbatim and the persist effect
  // already writes per league — so it survives reloads, is per-league by
  // construction, and needs no new storage key.
  //
  // It holds the IMPORT STAMP it was written for, not a boolean. A boolean
  // could only ever be consumed once per league: re-importing keeps the league
  // NAME, so useMockDraft's reload effect (keyed on activeLeagueName) never
  // re-runs and the in-memory configs carry the old marker straight through the
  // import — the panel would stay shut, and the persist effect would write the
  // stale flag back. Comparing against activeLeague.importedAt sidesteps the
  // stale copy entirely: a new stamp mismatches whatever is in memory.
  //
  // Written the moment the panel opens, so collapsing it is still final for the
  // life of that import — only a NEW import reopens it.
  const seatsSeeded = useMemo(
    () => opponentPool.some(n => mockTeamConfigs[n]?.customName?.trim()),
    [opponentPool, mockTeamConfigs]
  );
  const leagueHasSeats = !!(activeLeague?.members?.length && activeLeague?.auctionHistory?.length);
  // 0 for leagues imported before stamping existed — including one whose marker
  // is a leftover `true` from the first cut of this feature, which mismatches 0
  // and so gets its one open rather than staying stuck shut.
  const importStamp = activeLeague?.importedAt ?? 0;

  useEffect(() => {
    if (!leagueHasSeats || !seatsSeeded) return;
    if (mockTeamConfigs.__seatsSeen === importStamp) return;
    setShowExpert(true);
    setMockTeamConfigs(prev => (prev.__seatsSeen === importStamp ? prev : { ...prev, __seatsSeen: importStamp }));
  }, [leagueHasSeats, seatsSeeded, importStamp, mockTeamConfigs, setMockTeamConfigs]);

  const randomizeOpponents = () => {
    const archetypeKeys = MAIN_ARCHETYPES;
    const newConfigs = { ...mockTeamConfigs };
    opponentPool.forEach(name => {
      newConfigs[name] = {
        ...newConfigs[name],
        archetype: archetypeKeys[Math.floor(Math.random() * archetypeKeys.length)],
      };
    });
    setMockTeamConfigs(newConfigs);
  };

  // ── Keepers ────────────────────────────────────────────────────────────────
  // The picker seats are `opponentPool` — literally the same array the expert
  // panel renders now that both come from `opponentSeats`. This used to be a
  // second `opponentSeats(...)` call sitting deliberately apart from the panel's
  // wrong list; there is nothing left to hold apart.
  const keeperProblems = useMemo(
    () => validateKeepers(mockTeamConfigs, settings),
    [mockTeamConfigs, settings]
  );
  const keepersBlockStart = keeperProblems.length > 0;

  // ── Where the Keepers section lives ────────────────────────────────────────
  // Home is inside "Customize Opponents, Keepers & More": most users are not in
  // keeper leagues and should not carry the row on the front page. It AUTO-
  // PROMOTES to the main page when this league actually has keepers — either the
  // user entered one, or the import detected keeper history. That second case
  // promotes at ZERO entered keepers on purpose: an imported keeper league is
  // exactly the user who came here for this feature.
  //
  // The rule itself is `shouldPromoteKeeperSection` in utils/keepers, so it can
  // be unit- and mutation-tested on its own rather than only through a rendered
  // tree. It reuses leagueMarket's existing `keeperShare > 0.05` signal rather
  // than re-deriving one — the same number the market card already prints
  // "keeper league — N kept players excluded" from, so the two can never
  // disagree about whether this is a keeper league.
  //
  // LATCHED, NOT LIVE, and that is the whole subtlety. Recomputing per render
  // would move the section out from under someone mid-edit: delete your last
  // keeper and the panel you are typing in vanishes. So the decision is taken
  // once per visit and re-taken when the league changes (switching leagues IS a
  // new visit). Promotion and demotion are therefore symmetrical — both land on
  // the next visit. This is React's documented "adjust state when a prop
  // changes" pattern, not an effect, so there is no promoted-then-demoted flash.
  //
  // THE KEEPER-LEAGUE FLAG IS THE ONE EXCEPTION, and it is a narrow one. The
  // latch also re-takes when `isKeeperLeague` flips, because that flip is an
  // explicit answer to an explicit question ("keeper league? yes") rather than
  // a side effect of editing — a user who just ticked YES on the essentials
  // step means NOW, and would have no idea that the section they asked for
  // appears on some later visit. It cannot fire spuriously while someone is
  // mid-edit either: typing a keeper moves `hasKeepers`, never this flag.
  const computePromote = () => shouldPromoteKeeperSection(mockTeamConfigs, marketProfile, activeLeague);
  const keeperFlag = activeLeague?.isKeeperLeague === true;
  const [keeperPlacement, setKeeperPlacement] = useState(
    () => ({ league: activeLeagueName, flag: keeperFlag, promoted: computePromote() })
  );
  if (keeperPlacement.league !== activeLeagueName || keeperPlacement.flag !== keeperFlag) {
    setKeeperPlacement({ league: activeLeagueName, flag: keeperFlag, promoted: computePromote() });
  }
  const keeperPromoted = keeperPlacement.promoted;

  // A blocking keeper problem with the section tucked inside a COLLAPSED
  // Customize panel is the dead-button-with-no-explanation trap again, one level
  // deeper. Open the panel rather than move the section: the user keeps their
  // place, and the reason becomes visible.
  useEffect(() => {
    if (keepersBlockStart && !keeperPromoted) setShowExpert(true);
  }, [keepersBlockStart, keeperPromoted]);

  // ONE element, rendered at exactly one of two mount points. Built here rather
  // than written out twice so the two placements cannot drift in their props —
  // and so there is only ever one instance, which is what makes "same state"
  // true by construction rather than by discipline.
  const keeperEntry = (
    <KeeperEntry
      teamConfigs={mockTeamConfigs}
      setTeamConfigs={setMockTeamConfigs}
      settings={settings}
      userOwner={mockUserOwner}
      opponentKeys={opponentPool}
      canonicalRoom={_roomRule.canonical}
      problems={keeperProblems}
      forceOpen={keepersBlockStart}
    />
  );

  // "Has a league" means a league is CURRENT, not that one exists somewhere in
  // storage: savedLeagues is seeded with a default record for everyone, so
  // counting records would send a brand-new visitor to the settings modal.
  // A null activeLeagueName is what puts DEFAULT in the chip, and the DEFAULT
  // chip is precisely the user this button is for.
  const hasLeague = !!activeLeagueName;
  const startSetup = () => {
    captureSetupNudgeClicked("mock_setup");
    (onCreateLeague || onOpenSettings)();
  };

  const scoringLabel = settings.scoring.receptions === 0 ? "Standard"
    : settings.scoring.receptions === 0.5 ? "Half-PPR"
    : settings.scoring.receptions === 1 ? "PPR"
    : `${settings.scoring.receptions} PPR`;
  const rosterSize = Object.values(settings.positions).reduce((s, v) => s + v, 0);

  // Shared card renderer for both the YOUR TEAM ("__user") profile and each
  // opponent. `hideName` drops the NAME field (the user's name comes from the
  // team-name input); `label` overrides the collapsed-row display name.
  const renderTeamCard = (name, { hideName = false, label = null } = {}) => {
    const cfg = mockTeamConfigs[name] || {};
    const archKey = cfg.archetype || "Balanced";
    const displayName = label || cfg.customName?.trim() || name;
    const isEditing = editingTeam === name;
    const mods = cfg.modifiers || [];
    const arch = ARCHETYPES[archKey] || ARCHETYPES["Balanced"];
    const ovVal = cfg.overpay ?? arch.overpay;
    const meta = ARCHETYPE_META[archKey] || { icon: "🎲", desc: "" };
    // Mirrors buildDraftTeams' QB-strategy branch: this flag picks which ladder
    // the Fine Tuning panel OFFERS (QB_STRATS_SF vs QB_STRATS), so it has to
    // read the same predicate the engine assigns from, or a 2QB room shows a
    // strategy list that cannot represent what its seats were actually given.
    const isSflex = isQbPremiumFormat(settings?.positions);

    const updateCfg = (updates) => setMockTeamConfigs(prev => ({
      ...prev, [name]: { ...prev[name], ...updates },
    }));
    const toggleMod = (mod) => {
      const current = [...mods];
      const conflict = MODIFIERS[mod]?.conflicts || [];
      if (current.includes(mod)) {
        updateCfg({ modifiers: current.filter(m => m !== mod) });
      } else {
        updateCfg({ modifiers: [...current.filter(m => !conflict.includes(m)), mod] });
      }
    };

    return (
      <div key={name} style={{
        borderRadius: radius.md,
        border: `1px solid ${isEditing ? colors.border.brandLight : colors.border.subtle}`,
        background: isEditing ? "rgba(255,107,0,0.04)" : "transparent",
        overflow: "hidden",
        transition: "border-color 0.12s ease",
      }}>
        {/* Collapsed row */}
        <div style={{
          display: "flex", alignItems: "center", gap: spacing.sm,
          padding: `${spacing.md}px ${spacing.md}px`,
          cursor: "pointer",
        }} onClick={() => setEditingTeam(isEditing ? null : name)}>
          {/* Name */}
          <span style={{
            flex: 1, fontSize: fontSize.body, fontWeight: fontWeight.medium,
            color: colors.text.primary, overflow: "hidden", textOverflow: "ellipsis",
            whiteSpace: "nowrap", minWidth: 0,
          }}>{displayName}</span>

          {/* Archetype badge */}
          <span style={{
            display: "flex", alignItems: "center", gap: 4,
            fontSize: fontSize.micro, fontWeight: fontWeight.bold,
            color: meta.color, background: `${meta.color}1a`,
            border: `1px solid ${meta.color}40`,
            padding: `2px ${spacing.sm}px`, borderRadius: radius.sm,
            whiteSpace: "nowrap", flexShrink: 0,
          }}>
            <span style={{ fontSize: 11 }}>{meta.icon}</span>
            {archKey}
          </span>

          {/* Modifier pills */}
          {mods.length > 0 && (
            <div style={{ display: "flex", gap: 3, flexShrink: 0 }}>
              {mods.map(mod => (
                <span key={mod} style={{
                  fontSize: 9, fontWeight: fontWeight.bold,
                  color: colors.text.secondary,
                  background: colors.bg.deep,
                  padding: `1px 5px`, borderRadius: 3,
                  border: `1px solid ${colors.border.subtle}`,
                  whiteSpace: "nowrap",
                }}>{mod}</span>
              ))}
            </div>
          )}

          {/* Edit toggle */}
          <span style={{
            fontSize: fontSize.micro, color: isEditing ? colors.brand.accent : colors.text.faint,
            fontWeight: fontWeight.medium, flexShrink: 0,
          }}>{isEditing ? "Close" : "Edit ›"}</span>
        </div>

        {/* Inline edit panel */}
        {isEditing && (
          <div style={{
            padding: `${spacing.md}px ${spacing.lg}px ${spacing.xl}px`,
            borderTop: `1px solid ${colors.border.subtle}`,
            background: colors.bg.deep,
            animation: "fadeInUp 0.15s ease-out",
          }}>
            {/* Custom name */}
            {!hideName && (
              <div style={{ marginBottom: spacing.lg }}>
                <label style={{
                  display: "block", fontSize: fontSize.micro, fontWeight: fontWeight.bold,
                  color: colors.text.secondary, letterSpacing: "0.5px", marginBottom: spacing.xs,
                }}>NAME</label>
                <input type="text" placeholder={name} value={cfg.customName || ""}
                  onChange={e => updateCfg({ customName: e.target.value })}
                  style={{
                    width: "100%", padding: `${spacing.sm}px ${spacing.md}px`,
                    borderRadius: radius.sm, fontSize: fontSize.body, fontFamily: "inherit",
                    background: colors.bg.app, border: `1px solid ${colors.border.subtle}`,
                    color: colors.text.primary, boxSizing: "border-box",
                  }}
                />
              </div>
            )}

            {/* Archetype grid */}
            <div style={{ marginBottom: spacing.lg }}>
              <label style={{
                display: "block", fontSize: fontSize.micro, fontWeight: fontWeight.bold,
                color: colors.text.secondary, letterSpacing: "0.5px", marginBottom: spacing.sm,
              }}>ARCHETYPE</label>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: spacing.xs }}>
                {MAIN_ARCHETYPES.map(a => {
                  const m = ARCHETYPE_META[a] || { icon: "🎲", desc: "" };
                  const selected = archKey === a;
                  return (
                    <button key={a} onClick={() => updateCfg({ archetype: a, overpay: null })}
                      style={{
                        display: "flex", flexDirection: "column", alignItems: "flex-start",
                        gap: 2, padding: `${spacing.sm}px ${spacing.md}px`,
                        borderRadius: radius.sm, cursor: "pointer", textAlign: "left",
                        background: selected ? `${m.color}1a` : colors.bg.app,
                        border: `1px solid ${selected ? `${m.color}60` : colors.border.subtle}`,
                        transition: "all 0.1s ease",
                        fontFamily: "inherit",
                      }}>
                      <span style={{ fontSize: 14 }}>{m.icon}</span>
                      <span style={{
                        fontSize: fontSize.caption, fontWeight: fontWeight.bold,
                        color: selected ? m.color : colors.text.primary,
                        lineHeight: 1.2,
                      }}>{a}</span>
                      <span style={{
                        fontSize: 10, color: colors.text.muted, lineHeight: 1.3,
                        fontWeight: fontWeight.regular,
                      }}>{m.desc}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Fine-Tuning */}
            <FineTuning
              mods={mods}
              cfg={cfg}
              isSflex={isSflex}
              updateCfg={updateCfg}
              toggleMod={toggleMod}
            />

            {/* Overpay slider */}
            <div style={{ marginBottom: spacing.lg }}>
              <div style={{
                display: "flex", justifyContent: "space-between", alignItems: "center",
                marginBottom: spacing.xs,
              }}>
                <span style={{ fontSize: fontSize.micro, fontWeight: fontWeight.bold, color: colors.text.secondary, letterSpacing: "0.5px" }}>OVERPAY</span>
                <span style={{
                  fontSize: fontSize.small, fontWeight: fontWeight.heavy,
                  color: ovVal > 5 ? colors.status.danger : ovVal > 2 ? colors.brand.orange : colors.text.secondary,
                }}>{ovVal > 0 ? "+" : ""}{ovVal}%</span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: spacing.md }}>
                <span style={{ fontSize: fontSize.micro, color: colors.text.muted }}>-10</span>
                <input type="range" min={-10} max={15} step={0.5} value={ovVal}
                  onChange={e => updateCfg({ overpay: parseFloat(e.target.value) })}
                  style={{ flex: 1, accentColor: colors.brand.orange }}
                />
                <span style={{ fontSize: fontSize.micro, color: colors.text.muted }}>+15</span>
              </div>
            </div>

            {/* Close */}
            <button onClick={() => setEditingTeam(null)} style={{
              width: "100%", padding: `${spacing.sm}px`, borderRadius: radius.sm,
              fontSize: fontSize.caption, fontWeight: fontWeight.medium, fontFamily: "inherit",
              background: "transparent", color: colors.text.muted,
              border: `1px solid ${colors.border.subtle}`, cursor: "pointer",
            }}>Done</button>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="mock-setup-wrapper" style={{
      maxWidth: 420, margin: "0 auto", padding: `${spacing.xxxl}px ${spacing.xl}px`,
    }}>
      {/* Title */}
      <h2 style={{
        fontFamily: fonts.display, fontWeight: fontWeight.heavy, fontSize: 32,
        textAlign: "center", letterSpacing: "2px",
        background: colors.brand.gradient, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
        margin: 0, lineHeight: 1.1,
      }}>MOCK DRAFT</h2>
      <p style={{
        fontFamily: fonts.body, fontSize: fontSize.small, color: colors.text.secondary,
        textAlign: "center", marginTop: spacing.xs, marginBottom: spacing.xl,
      }}>Draft against opponents with unique strategies</p>

      {/* Settings summary */}
      <div style={{
        textAlign: "center", fontSize: fontSize.caption, color: colors.text.muted,
        marginBottom: spacing.xxl, letterSpacing: "0.3px",
      }}>
        {settings.teams} Teams &middot; ${settings.budget} &middot; {scoringLabel} &middot; {rosterSize} Roster
        {/* Opponent configs below are per-league — name the league they belong to. */}
        {" "}&middot;{" "}<LeagueChip name={activeLeagueName} />

        {/* ── The entry, as a real button ──────────────────────────────────
            This used to be two pieces of small inline text — a "Set up your
            league →" nudge and an "Edit" link — sharing a line with the
            format summary. Both were the size of a footnote, and the first
            one is the single most important thing on the screen for a
            visitor who has no league: it is where the app either onboards
            them or loses them.

            ONE control, two jobs, decided by whether a league is current:
              - no league (the DEFAULT chip): straight into the create flow,
                because the answer to "set up your league" is a name, not a
                settings modal with four tabs;
              - a league: League Settings, exactly as before.

            The nudge's setup_nudge_clicked event rides along on the first
            branch, so the conversion funnel keeps measuring the same click.
            Sized and placed to be above the fold on desktop and inside the
            420px wrapper alike — it is the width of the column either way. */}
        <div style={{ marginTop: spacing.md }}>
          <button
            className="btn"
            onClick={hasLeague ? onOpenSettings : startSetup}
            style={{
              padding: `${spacing.sm}px ${spacing.xl}px`, borderRadius: radius.md,
              fontSize: fontSize.caption, fontWeight: fontWeight.bold, fontFamily: fonts.display,
              letterSpacing: "0.5px", cursor: "pointer",
              background: hasLeague ? colors.bg.surface : "rgba(255,107,0,0.12)",
              color: hasLeague ? colors.text.secondary : colors.brand.accent,
              border: `1px solid ${hasLeague ? colors.border.subtle : colors.border.brandLight}`,
            }}
          >{hasLeague ? "Edit league settings" : "Set up your league →"}</button>
        </div>
      </div>

      {/* Your League's Room — what this league's own auctions look like, and
          the opt-in that points the opponents at them. Renders only with a
          profile, and carries its own bottom margin, so a league without
          history leaves no gap where it would have been. */}
      {marketProfile && (
        <div style={{
          background: colors.bg.surface, border: `1px solid ${colors.border.subtle}`,
          borderRadius: radius.xl, padding: spacing.xl, marginBottom: spacing.xxl,
        }}>
          <div style={{
            display: "flex", alignItems: "center", gap: spacing.sm,
            flexWrap: "wrap", marginBottom: spacing.sm,
          }}>
            <div style={{
              fontFamily: fonts.display, fontWeight: fontWeight.bold, fontSize: fontSize.sub,
              color: colors.text.primary,
            }}>
              Customize this mock to {activeLeagueName}'s history
            </div>
            {/* The only time-limit signal users get before these gates flip, so
                it sits on the header next to the calibration affordance rather
                than behind Details — muted, informational, not a promo badge.
                Wording is load-bearing: "Pro features" is what prices the tier
                and "during beta" is what keeps it from reading as free forever.
                Don't soften to "free for now" or "beta" alone. */}
            <span style={{
              flexShrink: 0, padding: `2px ${spacing.sm}px`, borderRadius: radius.sm,
              background: colors.bg.raised, border: `1px solid ${colors.border.subtle}`,
              fontSize: fontSize.micro, color: colors.text.muted, whiteSpace: "nowrap",
            }}>Pro features — free during beta</span>
          </div>

          {/* Verdicts, not arithmetic. The raw numbers live under Details. */}
          <div style={{
            fontSize: fontSize.body, color: colors.text.secondary,
            lineHeight: 1.5, marginBottom: spacing.xs,
          }}>
            {marketClauses.map((c, i) => (i === 0 ? c.charAt(0).toUpperCase() + c.slice(1) : c)).join(" · ")}
          </div>
          {/* Keeper leagues get told what's missing from these numbers: kept
              players are carried contracts, so they're excluded from every
              figure above. Below 5% it's a rounding detail, not worth the
              extra clause. */}
          <div style={{ fontSize: fontSize.micro, color: colors.text.muted, marginBottom: spacing.lg }}>
            based on {marketProfile.nYears} auction{marketProfile.nYears !== 1 ? "s" : ""}, {marketProfile.nPicks} picks
            {marketProfile.keeperShare > 0.05 &&
              ` · keeper league — ${marketProfile.keeperCount} kept player${marketProfile.keeperCount !== 1 ? "s" : ""} excluded`}
          </div>

          <label style={{
            display: "flex", alignItems: "center", gap: spacing.sm, cursor: "pointer",
            fontSize: fontSize.body, color: colors.text.primary,
          }}>
            <input
              type="checkbox"
              checked={activeLeague?.calibrate === true}
              onChange={e => onSetCalibrate?.(e.target.checked)}
              style={{ cursor: "pointer", accentColor: colors.brand.accent, width: 15, height: 15 }}
            />
            Calibrate prices & opponents to this history
          </label>

          {/* Only meaningful with calibration on and at least one manager
              fitted — a "0 of 12" line would advertise a feature doing
              nothing. m counts leaguemates, not seats, so it reads as a
              statement about the league rather than about the mock. */}
          {activeLeague?.calibrate === true && modeledSeatCount > 0 && (
            <div style={{ marginTop: spacing.xs, fontSize: fontSize.micro, color: colors.text.muted }}>
              Opponents modeled: {modeledSeatCount} of {activeLeague.members?.length ?? 0} leaguemates
            </div>
          )}

          {/* Quiet on purpose — the receipts behind the summary above, for
              users who want to check the claim, not a second call to action
              competing with the toggle. */}
          {activeLeague?.auctionHistory?.length > 0 && (
            <div
              onClick={() => setShowHistory(true)}
              style={{
                marginTop: spacing.md, fontSize: fontSize.micro,
                color: colors.text.muted, cursor: "pointer", width: "fit-content",
              }}
            >View draft history →</div>
          )}
        </div>
      )}

      <DraftHistoryModal
        isOpen={showHistory}
        onClose={() => setShowHistory(false)}
        leagueName={activeLeagueName}
        history={activeLeague?.auctionHistory}
        members={activeLeague?.members}
      />


      {/* Saved draft resume banner */}
      {mockSavedDraft && (
        <div style={{
          background: `linear-gradient(135deg, rgba(255,107,0,0.12), rgba(255,107,0,0.04))`,
          border: `1px solid ${colors.border.brand}`, borderRadius: radius.xl,
          padding: spacing.xxl, textAlign: "center", marginBottom: spacing.xxl,
        }}>
          <div style={{
            fontFamily: fonts.display, fontWeight: fontWeight.bold, fontSize: fontSize.heading,
            color: colors.brand.accent, marginBottom: spacing.xs,
          }}>Draft in Progress</div>
          <p style={{ color: colors.text.secondary, fontSize: fontSize.body, marginBottom: spacing.xl }}>
            Pick {mockSavedDraft.mockPickNumber} of {rosterSize * teamCount} &mdash; {mockSavedDraft.mockDraftLog?.length || 0} players drafted
          </p>
          <div style={{ display: "flex", gap: spacing.lg, justifyContent: "center" }}>
            <button className="btn" onClick={resumeMockDraft} style={{
              padding: `${spacing.md}px ${spacing.xxxl}px`, borderRadius: radius.md,
              fontSize: fontSize.sub, fontWeight: fontWeight.bold, fontFamily: fonts.display,
              background: colors.brand.gradient, color: "#fff", border: "none", cursor: "pointer",
            }}>Resume</button>
            <button className="btn" onClick={onClearSavedDraft} style={{
              padding: `${spacing.md}px ${spacing.xxxl}px`, borderRadius: radius.md,
              fontSize: fontSize.sub, fontWeight: fontWeight.bold, fontFamily: fonts.display,
              background: "rgba(239,68,68,0.1)", color: colors.status.danger,
              border: `1px solid rgba(239,68,68,0.2)`, cursor: "pointer",
            }}>New Draft</button>
          </div>
        </div>
      )}

      {/* Team name input */}
      <div style={{ marginBottom: spacing.xxl }}>
        <label style={{
          display: "block", fontSize: fontSize.caption, fontWeight: fontWeight.bold,
          color: colors.text.secondary, letterSpacing: "1px", marginBottom: spacing.sm,
          textAlign: "center",
        }}>YOUR TEAM NAME</label>
        <input
          type="text"
          value={mockUserOwner}
          onChange={e => setMockUserOwner(e.target.value)}
          placeholder="Enter team name..."
          className="input-field"
          style={{
            width: "100%", padding: `${spacing.lg}px ${spacing.xl}px`,
            borderRadius: radius.md, fontSize: fontSize.sub, fontFamily: "inherit",
            textAlign: "center", fontWeight: fontWeight.medium,
            background: colors.bg.surface, border: `1px solid ${colors.border.subtle}`,
            color: colors.text.primary, boxSizing: "border-box",
          }}
        />
      </div>

      {/* MOUNT POINT 1 — the main page, above START DRAFT so the reason the
          button is disabled sits next to it. Rendered ONLY when promoted; the
          other mount point inside Customize carries the `!keeperPromoted` half
          of the same boolean, so the section can never appear twice. */}
      {keeperPromoted && keeperEntry}

      {/* START DRAFT */}
      <button
        className="btn mock-setup-start"
        onClick={keepersBlockStart ? undefined : startMockDraft}
        disabled={keepersBlockStart}
        title={keepersBlockStart ? "Fix the keeper problems above first" : undefined}
        style={{
          width: "100%", padding: `${spacing.xl}px`, borderRadius: radius.lg,
          fontSize: 18, fontWeight: fontWeight.heavy, fontFamily: fonts.display,
          letterSpacing: "1.5px", marginTop: spacing.xxl,
          background: keepersBlockStart ? colors.bg.surface : colors.brand.gradientBtn,
          color: keepersBlockStart ? colors.text.muted : "#fff",
          border: keepersBlockStart ? `1px solid ${colors.border.subtle}` : "none",
          boxShadow: keepersBlockStart ? "none" : "0 4px 20px rgba(255,107,0,0.3)",
          cursor: keepersBlockStart ? "not-allowed" : "pointer",
        }}>START DRAFT</button>
      {keepersBlockStart && (
        <p style={{
          textAlign: "center", fontSize: fontSize.micro, color: "#f87171",
          marginTop: spacing.sm, marginBottom: 0,
        }}>
          {keeperProblems.length} keeper problem{keeperProblems.length === 1 ? "" : "s"} to fix
          {/* When the section lives in Customize, the problems are not on this
              screen — say where they are. The panel is force-opened above, so
              this is a pointer, not a scavenger hunt. */}
          {keeperPromoted
            ? <> &mdash; a draft that silently drops a keeper is worse than one that won&rsquo;t start.</>
            : <> &mdash; see Keepers, under Customize Opponents, Keepers &amp; More below.</>}
        </p>
      )}

      {/* QUICK SIM \u2014 gated on the same problems. It runs the same engine path, so
          letting it through would just move the silent keeper drop somewhere the
          user is even less likely to notice. */}
      <button className="btn"
        onClick={keepersBlockStart ? undefined : () => onSimulate([])}
        disabled={keepersBlockStart}
        title={keepersBlockStart ? "Fix the keeper problems above first" : undefined}
        style={{
          width: "100%", padding: `${spacing.lg}px`, borderRadius: radius.lg,
          fontSize: fontSize.sub, fontWeight: fontWeight.bold, fontFamily: fonts.display,
          letterSpacing: "1px", marginTop: spacing.lg,
          background: colors.bg.surface,
          color: keepersBlockStart ? colors.text.muted : colors.text.primary,
          border: `1px solid ${keepersBlockStart ? colors.border.subtle : colors.border.brandLight}`,
          cursor: keepersBlockStart ? "not-allowed" : "pointer",
        }}>{"\u26A1"} QUICK SIM</button>
      <p style={{
        textAlign: "center", fontSize: fontSize.micro, color: colors.text.muted,
        marginTop: spacing.sm,
      }}>Drafts your seat instantly using a default strategy</p>

      {/* Expert toggle */}
      <button className="btn" onClick={() => setShowExpert(v => !v)} style={{
        width: "100%", padding: `${spacing.lg}px`, borderRadius: radius.lg,
        fontSize: fontSize.caption, fontWeight: fontWeight.bold, fontFamily: fonts.display,
        letterSpacing: "0.5px", marginTop: spacing.xxl,
        background: showExpert ? "rgba(255,107,0,0.1)" : colors.bg.surface,
        color: showExpert ? colors.brand.accent : colors.text.secondary,
        border: `1px solid ${showExpert ? colors.border.brandLight : colors.border.subtle}`,
        cursor: "pointer",
      }}>{showExpert ? "\u25B4" : "\u25BE"} Customize Opponents, Keepers & More</button>

      {/* Expert panel */}
      {showExpert && (
        <div style={{
          marginTop: spacing.lg, padding: spacing.xl,
          background: colors.bg.surface, border: `1px solid ${colors.border.subtle}`,
          borderRadius: radius.xl, animation: "fadeInUp 0.2s ease-out",
        }}>
          {/* Position price dials. Inside the expert panel, near the calibrate
              toggle, because it is the same kind of decision: how this league
              prices positions. */}
          {onSetPositionDials && (
            <PositionDials
              dials={positionDials}
              onChange={onSetPositionDials}
              overridesLeagueHistory={dialsOverrideLeague}
            />
          )}

          {/* Market anchor. Sits with the dials because it answers the other
              half of the same question: the dials say which POSITIONS this room
              pays up for, this says how high the top of the room goes at all.
              Unlike the dials it never touches the board — only how hard the AI
              bids — which is why its copy is clearing language throughout. */}
          {onSetMarketAnchor && (
            <MarketAnchor
              anchor={marketAnchor}
              onChange={onSetMarketAnchor}
              echo={marketAnchorEcho}
              /* COMPUTED from this room's budget, never a hardcoded dollar. A
                 literal "63.5" here would re-introduce the budget-unit bug one
                 layer up: a $240 superflex room's default is $76/$65, and the
                 user has to see the number their room actually assumes. */
              formatDefault={defaultAnchorForFormat(settings.positions, settings.budget)}
              /* Budget-scaled here for the same reason `formatDefault` is: the
                 chips must offer this room's numbers, not a $200 room's. In a
                 $240 league Sleeper is $84 / $69, not $70 / $57.50. */
              presetPairs={marketAnchorPresetPairs(settings.budget)}
              /* DERIVED from the pair in force, never stored — so the lit chip
                 is a statement about what the room will actually bid, and the
                 `anchor_preset` analytics property (useMockDraft.startMockDraft
                 and the Quick Sim path) reads the SAME function on the SAME
                 inputs and cannot disagree with the screen. */
              preset={marketAnchorPresetFor(marketAnchor, settings.positions, settings.budget)}
            />
          )}

          {/* Slot + Speed + Nomination clock row (auto-fit so it wraps on narrow screens) */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: spacing.lg, marginBottom: spacing.xxl }}>
            <div>
              <label style={{
                display: "block", fontSize: fontSize.micro, fontWeight: fontWeight.bold,
                color: colors.text.secondary, letterSpacing: "1px", marginBottom: spacing.xs,
              }}>NOMINATION SLOT</label>
              <select value={mockUserSlot} onChange={e => setMockUserSlot(Number(e.target.value))} style={{
                width: "100%", padding: `${spacing.md}px ${spacing.md}px`,
                borderRadius: radius.sm, fontSize: fontSize.body, fontWeight: fontWeight.medium,
                fontFamily: "inherit", background: colors.bg.deep, color: colors.text.primary,
                border: `1px solid ${colors.border.subtle}`, cursor: "pointer",
              }}>
                {Array.from({ length: teamCount }, (_, i) => (
                  <option key={i} value={i}>{i + 1}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={{
                display: "block", fontSize: fontSize.micro, fontWeight: fontWeight.bold,
                color: colors.text.secondary, letterSpacing: "1px", marginBottom: spacing.xs,
              }}>DRAFT SPEED</label>
              <select value={mockSpeed} onChange={e => setMockSpeed(e.target.value)} style={{
                width: "100%", padding: `${spacing.md}px ${spacing.md}px`,
                borderRadius: radius.sm, fontSize: fontSize.body, fontWeight: fontWeight.medium,
                fontFamily: "inherit", background: colors.bg.deep, color: colors.text.primary,
                border: `1px solid ${colors.border.subtle}`, cursor: "pointer",
              }}>
                {Object.entries(SPEED_SETTINGS).map(([key, s]) => (
                  <option key={key} value={key}>{s.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={{
                display: "block", fontSize: fontSize.micro, fontWeight: fontWeight.bold,
                color: colors.text.secondary, letterSpacing: "1px", marginBottom: spacing.xs,
              }}>NOMINATION CLOCK</label>
              <select
                value={String(mockNomTimerMode)}
                onChange={e => {
                  const v = e.target.value;
                  setMockNomTimerMode(v === "off" || v === "preset" ? v : Number(v));
                }}
                style={{
                  width: "100%", padding: `${spacing.md}px ${spacing.md}px`,
                  borderRadius: radius.sm, fontSize: fontSize.body, fontWeight: fontWeight.medium,
                  fontFamily: "inherit", background: colors.bg.deep, color: colors.text.primary,
                  border: `1px solid ${colors.border.subtle}`, cursor: "pointer",
                }}>
                <option value="off">No limit</option>
                <option value="15">15s</option>
                <option value="30">30s</option>
                <option value="60">60s</option>
                <option value="preset">Match speed</option>
              </select>
            </div>
          </div>

          {/* MOUNT POINT 2 — the default home. Placed above YOUR TEAM rather
              than at the bottom of the panel: a keeper is a fact about the league
              you are setting up, so it belongs with the league-shaped controls
              (slot, speed, clock) rather than after twelve opponent cards.
              Renders only when NOT promoted, so exactly one mount point is live. */}
          {!keeperPromoted && (
            <div style={{ marginBottom: spacing.xxl, marginTop: `-${spacing.lg}px` }}>
              {keeperEntry}
            </div>
          )}

          {/* Your team header + card */}
          <div style={{ marginBottom: spacing.xs }}>
            <span style={{
              fontSize: fontSize.caption, fontWeight: fontWeight.bold,
              color: colors.brand.accent, letterSpacing: "1px",
            }}>YOUR TEAM</span>
            <span style={{ fontSize: 10, color: colors.text.muted, marginLeft: spacing.sm }}>
              — used when Auto-Bid or Quick Sim drafts for you
            </span>
          </div>
          <div style={{ marginBottom: spacing.xxl }}>
            {renderTeamCard("__user", { hideName: true, label: mockUserOwner || "Your Team" })}
          </div>

          {/* Opponents header */}
          <div style={{
            display: "flex", justifyContent: "space-between", alignItems: "center",
            marginBottom: spacing.md,
          }}>
            <span style={{
              fontSize: fontSize.caption, fontWeight: fontWeight.bold,
              color: colors.text.secondary, letterSpacing: "1px",
            }}>OPPONENTS</span>
            <button className="btn" onClick={randomizeOpponents} style={{
              padding: `${spacing.xs}px ${spacing.lg}px`, borderRadius: radius.sm,
              fontSize: fontSize.micro, fontWeight: fontWeight.medium,
              fontFamily: "inherit", background: "transparent",
              color: colors.brand.accent, border: `1px solid ${colors.border.brandLight}`,
              cursor: "pointer",
            }}>{"\uD83C\uDFB2"} Randomize All</button>
          </div>

          {/* Opponent list. The testid is how the panel-vs-scoreboard invariant
              is asserted against a RENDERED tree rather than against the array
              that feeds it — the bug this replaced was a wrong list rendering
              perfectly, so proving the source is not the same as proving the
              screen. See mockSetupSeats.test.js. */}
          <div data-testid="opponent-list" style={{ display: "flex", flexDirection: "column", gap: spacing.xs }}>
            {opponentPool.map(name => renderTeamCard(name))}
          </div>
        </div>
      )}
    </div>
  );
});

export default MockSetup;
